import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, Dictionary, EntityState } from '@reduxjs/toolkit';
import { mapValues } from 'lodash';

import createSafeContext from '#/utils/createSafeContext';
import { MarketSummaryNotification } from '#/api/ws/ws';
import useLiveConnection, {
  LiveConnectionBaseState,
} from '#/utils/useLiveConnection';
import {
  MarketsState,
  useOptionMarketsState,
} from '#/features/options/markets-context';
import * as MarketFn from '#/features/perpetuals/market-info/functions';
import {
  OptionMarketSummary,
  GetMarketsSummaryResp,
  getMarketsSummary,
} from '#/api/markets-summary';
import { OptionMarket } from '#/api/markets';
import * as OptionFn from '#/features/options/functions';

const selectId = (summary: OptionMarketSummary) => summary.symbol;

const adapter = createEntityAdapter<OptionMarketSummary>({
  selectId,
});
const selectors = adapter.getSelectors();

const INITIAL_STATE: MarketSummariesState = {
  status: 'connected',
  error: '',
  marketSummaries: adapter.getInitialState(),
};

export interface MarketSummariesState extends LiveConnectionBaseState {
  readonly marketSummaries: EntityState<OptionMarketSummary>;
}
interface MarketSummariesView
  extends Omit<MarketSummariesState, 'marketSummaries'> {
  readonly marketSummaries: Dictionary<MarketSummaryView>;
}

export const [useMarketSummariesState, MarketSummariesStateCtx] =
  createSafeContext<MarketSummariesState>('MarketSummariesState');
export const [useMarketSummariesView, MarketSummariesViewCtx] =
  createSafeContext<MarketSummariesView>('MarketSummariesView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function MarketSummariesStateProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);

  useMarketSummariesLiveConnection(setState);

  return (
    <MarketSummariesStateCtx value={state}>
      {props.children}
    </MarketSummariesStateCtx>
  );
}

export function MarketSummariesViewProvider(props: ProviderProps) {
  const marketSummariesState = useMarketSummariesState();
  const marketsState = useOptionMarketsState();

  const view = useMemo(
    () => createView(marketSummariesState, marketsState),
    [marketSummariesState, marketsState],
  );

  return (
    <MarketSummariesViewCtx value={view}>
      {props.children}
    </MarketSummariesViewCtx>
  );
}

interface MarketSummaryView {
  readonly symbol: string;
  readonly volume: string;
  readonly oraclePrice: string;
}

export function createView(
  marketSummariesState: MarketSummariesState,
  marketsState: MarketsState,
): MarketSummariesView {
  if (marketsState.status !== 'done') {
    return {
      status: marketsState.status,
      error: marketsState.error,
      marketSummaries: {},
    };
  }

  const entities = selectors.selectEntities(
    marketSummariesState.marketSummaries,
  );
  const marketSummaries = mapValues(entities, (summary: OptionMarketSummary) =>
    marketSummaryView(summary, marketsState.markets.entities),
  ) as unknown as Dictionary<MarketSummaryView>;

  return {
    ...marketSummariesState,
    marketSummaries,
  };
}

const emptyMarketSummaryView = (
  summary: OptionMarketSummary,
): MarketSummaryView => ({
  symbol: summary.symbol,
  volume: '-',
  oraclePrice: '-',
});

function marketSummaryView(
  summary: OptionMarketSummary,
  markets: Dictionary<OptionMarket>,
): MarketSummaryView {
  const market = markets[summary.symbol];

  if (market == null) return emptyMarketSummaryView(summary);

  const quoteCurrencyDecimalPlaces =
    MarketFn.quoteCurrencyDecimalPlaces(market);

  const volume = `$${summary.volume_24h.toFormat(quoteCurrencyDecimalPlaces)}`;

  const oraclePrice =
    summary.oracle_price != null
      ? `$${summary.oracle_price.toFormat(quoteCurrencyDecimalPlaces)}`
      : '-';

  return {
    symbol: summary.symbol,
    volume,
    oraclePrice,
  };
}

function useMarketSummariesLiveConnection(
  setState: Dispatch<SetStateAction<MarketSummariesState>>,
): void {
  const channel = `markets_summary`;

  const fetchAll = useCallback(async (signal: AbortSignal) => {
    const resp = await getMarketsSummary({
      market: 'ALL',
      signal,
    });

    if (!resp.ok) return resp;

    return {
      ...resp,
      data: {
        results: resp.data.results.filter(OptionFn.isOptionMarketSummary),
      },
    };
  }, []);

  const setAll = useCallback(
    (data: GetMarketsSummaryResp) => {
      setState((state) => ({
        ...state,
        marketSummaries: adapter.setAll(state.marketSummaries, data.results),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      marketSummaries: adapter.removeAll(state.marketSummaries),
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: MarketSummaryNotification) => {
      if (!OptionFn.isOptionMarketSummary(notif.data)) return;

      setState((state) => ({
        ...state,
        marketSummaries: adapter.setOne(state.marketSummaries, notif.data),
      }));
    },
    [setState],
  );

  useLiveConnection<
    GetMarketsSummaryResp,
    MarketSummariesState,
    MarketSummaryNotification
  >({
    setState,
    channel,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}
